import {
  useDisclosure,
  Center,
  Alert,
  AlertIcon,
  Button,
  Flex,
  Heading,
  Spacer,
  Stack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Link,
  useToast,
} from '@chakra-ui/react';
import { Api } from 'api';
import {
  ExpertiseBodyworkDisControlSelector,
  ExpertiseBodyworkExpertiseChassisDrawer,
  ExpertiseBodyworkExpertiseDrawer,
  ExpertiseBodyworkIcControlSelector,
  ExpertiseBodyworkMicronRecorder,
} from 'apps/expertise/components';
import { ExpertiseTramer, LoadingText, NoteAutoCompleteSelect, MasterNotesBanner } from 'components';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  ApplicationToggleType,
  CustomerQueryCacheKey,
  ExpertiseDamageNoteType,
  ExpertisePackageAbilityDefinitionType,
  IntegrationQueryCacheKey,
  ProductType,
  ProductTypeQueryCacheKey,
  TramerQueryType,
} from 'types';
import React, { useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { createCustomerSocket, createToggleSocket, useCheckAbilityCompleted } from 'core';
import { Trans } from '@lingui/macro';

export const ExpertiseBodyworkInformationPage: React.FC = () => {
  const customer = useSelector((state: RootState) => state.customer.focusedCustomer);
  const {
    isOpen: isExpertiseTramerWarningOpen,
    onOpen: onExpertiseTramerWarningOpen,
    onClose: onExpertiseTramerWarningClose,
  } = useDisclosure();
  const { isOpen: isExpertiseOpen, onOpen: onExpertiseOpen, onClose: onExpertiseClose } = useDisclosure();
  const { isOpen: isMicronRecoredOpen, onOpen: onMicronRecorderOpen, onClose: onMicronRecoredClose } = useDisclosure();
  const {
    isOpen: isExpertiseTramerModalOpen,
    onOpen: onExpertiseTramerModalOpen,
    onClose: onExpertiseTramerModalClose,
  } = useDisclosure();
  const {
    isOpen: isExpertiseTramer360ModalOpen,
    onOpen: onExpertiseTramer360ModalOpen,
    onClose: onExpertiseTramer360ModalClose,
  } = useDisclosure();
  const {
    isOpen: isChassisControlOpen,
    onOpen: onChassisControlOpen,
    onClose: onChassisControlClose,
  } = useDisclosure();
  const { isOpen: isIcKontrolOpen, onOpen: onIcKontrolOpen, onClose: onIcKontrolClose } = useDisclosure();
  const { isOpen: isDisKontrolOpen, onOpen: onDisKontrolOpen, onClose: onDisKontrolClose } = useDisclosure();

  const canCustomerContinue = useSelector((state: RootState) => state.customer.canCustomerContinue);
  const customerAbilityDefinitionTable = useSelector(
    (state: RootState) => state.customer.customerAbilityDefinitionTable,
  );
  const toast = useToast();

  const [isHasarKaydiEnabled, setIsHasarKaydiEnabled] = React.useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customerSocket, setCustomerSocket] = useState<Socket | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [toggleSocket, setToggleSocket] = useState<Socket | null>(null);

  const { data: customerVehicle, isLoading } = useQuery(
    CustomerQueryCacheKey.GetCustomerVehicle,
    async () => {
      const response = await Api.Customer.getCustomerVehicle(customer!.id);
      if (!response.data) {
        return null;
      }
      const getCustomerVehicleResponse = response.data;
      return getCustomerVehicleResponse.customerVehicle;
    },
    {
      cacheTime: 0,
      retry: 0,
      enabled: customer != null,
      refetchOnWindowFocus: false,
    },
  );
  const { data: didQueriedDamageBefore } = useQuery(
    [IntegrationQueryCacheKey.GetTramerOldQueries, customer!.id],
    async () => {
      const { data } = await Api.Integration.getOldTramerQueries(customer!.id, TramerQueryType.Damage);
      return data && data.length > 0;
    },
    {
      cacheTime: 0,
      retry: 0,
      enabled: customer != null && customerVehicle != null && customerVehicle.brandId != null && canCustomerContinue,
      refetchOnWindowFocus: false,
    },
  );

  const { data: queryTramer360Before } = useQuery(
    [IntegrationQueryCacheKey.GetTramerOldQueriesTramer360, customer!.id],
    async () => {
      const { data } = await Api.Integration.getOldTramerQueries(customer!.id, TramerQueryType.Tramer360Chassis);
      return data && data.length > 0 ? data[0] : undefined;
    },
    {
      cacheTime: 0,
      retry: 0,
      enabled: customer != null && customerVehicle != null && customerVehicle.brandId != null && canCustomerContinue,
      refetchOnWindowFocus: false,
    },
  );

  const { data: TramerResultProductPrices } = useQuery(
    ProductTypeQueryCacheKey.GetTramerQuery,
    async () => {
      const response = await Api.Util.getProductPriceByType(ProductType.TramerQuery);
      return response.data;
    },
    {
      enabled: isExpertiseTramerWarningOpen,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onError: (error: any) => {
        const {
          response: {
            data: { message },
          },
        } = error;
        toast({
          title: message,
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  useQuery(
    ['getToggleEnabledHasarKaydi', ApplicationToggleType.DamageQuery],
    async () => {
      const response = await Api.Util.getToggleEnabled(ApplicationToggleType.DamageQuery);
      return response?.data;
    },
    {
      onSuccess: (data) => {
        setIsHasarKaydiEnabled(data?.isEnabled);
      },
      onError: () => {
        toast({
          title: 'Şu an oto sorgudaki arıza sebebi ile hasar kaydı sorgulama işleminiz yapılmamıştır',
          status: 'error',
        });
      },
      refetchOnWindowFocus: false,
    },
  );

  const { data: isExpertiseTramerResultCompleted, refetch: refetchIsExpertiseTramerResultCompleted } =
    useCheckAbilityCompleted(customer?.id, ExpertisePackageAbilityDefinitionType.HasarKaydiSorgulama);

  const { data: isExpertiseTramer360ResultCompleted, refetch: refetchIsExpertiseTramer360ResultCompleted } =
    useCheckAbilityCompleted(customer?.id, ExpertisePackageAbilityDefinitionType.DetayliAracSorgulamaTramer360);

  const { data: isMicronRecorderResultsCompleted, refetch: refetchIsMicronRecorderResultsCompleted } =
    useCheckAbilityCompleted(customer?.id, ExpertisePackageAbilityDefinitionType.MikronKaydiFormu);

  const { data: isExpertiseResultsCompleted, refetch: refetchIsExpertiseResultsCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.BoyaDegisenFormu,
  );

  const { data: isChassisControlResultsCompleted, refetch: refetchIsChassisControlResultsCompleted } =
    useCheckAbilityCompleted(customer?.id, ExpertisePackageAbilityDefinitionType.SasiKontrol);

  const { data: isIcKontrolFormuCompleted, refetch: refetchisIcKontrolFormuCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.IcKontrolFormu,
  );

  const { data: isDisKontrolFormuCompleted, refetch: refetchisDisKontrolFormuCompleted } = useCheckAbilityCompleted(
    customer?.id,
    ExpertisePackageAbilityDefinitionType.DisKontrolFormu,
  );

  const onExpertiseTramerButtonClicked = () => {
    if (didQueriedDamageBefore) {
      onExpertiseTramerModalOpen();
    } else {
      onExpertiseTramerWarningOpen();
    }
  };

  useEffect(() => {
    if (customer) {
      const newCustomerSocket = createCustomerSocket(customer.id.toString());
      setCustomerSocket(newCustomerSocket);

      newCustomerSocket.on(
        'customerAbilityCompleted',
        ({ customerId, abilityDefinitionType }: { customerId: number; abilityDefinitionType: number }) => {
          if (Number(customerId) === customer.id) {
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.HasarKaydiSorgulama) {
              refetchIsExpertiseTramerResultCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.DetayliAracSorgulamaTramer360) {
              refetchIsExpertiseTramer360ResultCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.MikronKaydiFormu) {
              refetchIsMicronRecorderResultsCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.BoyaDegisenFormu) {
              refetchIsExpertiseResultsCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.SasiKontrol) {
              refetchIsChassisControlResultsCompleted();
            }
            if (abilityDefinitionType === ExpertisePackageAbilityDefinitionType.IcKontrolFormu) {
              refetchisIcKontrolFormuCompleted();
            }
          }
        },
      );

      return () => {
        newCustomerSocket.disconnect();
      };
    }
    return undefined;
  }, [customer]);

  useEffect(() => {
    const newToggleSocket = createToggleSocket();
    setToggleSocket(newToggleSocket);

    newToggleSocket.on('toggleChanged', (data: { toggleType: number; isEnabled: boolean }) => {
      if (data?.toggleType === ApplicationToggleType.DamageQuery) {
        setIsHasarKaydiEnabled(data?.isEnabled);
      }
    });

    return () => {
      newToggleSocket.disconnect();
    };
  }, []);

  if (isLoading || !customer) {
    return (
      <Center>
        <LoadingText />
      </Center>
    );
  }

  let micronAppUrl = '';
  if (
    customerVehicle != null &&
    customerVehicle.vehicleTypeId > 0 &&
    customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.MikronKaydiFormu] != null
  ) {
    const micronAppUrlBuilder = new URL('deeplink://https://ekspertiz.occocar.com/');
    micronAppUrlBuilder.searchParams.append('token', JSON.parse(localStorage.authUserInfo).token);
    micronAppUrlBuilder.searchParams.append('customerId', customer!.id.toString());
    if (customerVehicle != null && customerVehicle.vehicleTypeId > 0)
      micronAppUrlBuilder.searchParams.append('vehicleTypeId', customerVehicle!.vehicleTypeId.toString());
    micronAppUrlBuilder.searchParams.append('expertiseBodyDamagePartTypeId', '2');
    micronAppUrl = micronAppUrlBuilder.href;
  } else {
    micronAppUrl = '#';
  }

  return (
    <>
      {customer && customerVehicle && customerVehicle.vehicleType && (
        <>
          <ExpertiseBodyworkExpertiseDrawer
            isOpen={isExpertiseOpen}
            onClose={() => {
              onExpertiseClose();
              refetchIsExpertiseResultsCompleted();
            }}
            carBodyType={customerVehicle.vehicleType.name}
            isDisabled={!canCustomerContinue}
          />
          <ExpertiseBodyworkMicronRecorder
            isOpen={isMicronRecoredOpen}
            onClose={() => {
              onMicronRecoredClose();
              refetchIsMicronRecorderResultsCompleted();
            }}
            carBodyType={customerVehicle.vehicleType.name}
            customerId={customer.id}
            vehicleTypeId={customerVehicle.vehicleTypeId}
            isDisabled={!canCustomerContinue}
          />
          <ExpertiseBodyworkExpertiseChassisDrawer
            isOpen={isChassisControlOpen}
            onClose={() => {
              onChassisControlClose();
              refetchIsChassisControlResultsCompleted();
            }}
            carBodyType={customerVehicle.vehicleType.name}
            isDisabled={!canCustomerContinue}
          />
          <ExpertiseBodyworkIcControlSelector
            isOpen={isIcKontrolOpen}
            onClose={() => {
              onIcKontrolClose();
              refetchisIcKontrolFormuCompleted();
            }}
            isDisabled={!canCustomerContinue}
          />
          <ExpertiseBodyworkDisControlSelector
            isOpen={isDisKontrolOpen}
            onClose={() => {
              onDisKontrolClose();
              refetchisDisKontrolFormuCompleted();
            }}
            isDisabled={!canCustomerContinue}
          />
        </>
      )}
      {customerVehicle && customerVehicle.chassisNumber && (
        <>
          <ExpertiseTramer
            isOpen={isExpertiseTramerModalOpen}
            onClose={() => {
              onExpertiseTramerModalClose();
              refetchIsExpertiseTramerResultCompleted();
            }}
            customerId={customer.id}
          />
          {TramerResultProductPrices && (
            <Modal isOpen={isExpertiseTramerWarningOpen} onClose={onExpertiseTramerWarningClose} isCentered>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>
                  <Trans>Hasar Sorgu Onay?</Trans>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text>
                    <Trans>
                      Hasar Kaydı Sorgusu için ₺
                      {TramerResultProductPrices.buyPrice > 0
                        ? TramerResultProductPrices.buyPrice
                        : TramerResultProductPrices.price}{' '}
                      sorgu bedeli kullanılacaktır. Devam etmek istiyor musunuz ?
                    </Trans>
                  </Text>
                </ModalBody>
                <ModalFooter>
                  <Button
                    colorScheme="blue"
                    mr={3}
                    onClick={() => {
                      onExpertiseTramerModalOpen();
                      onExpertiseTramerWarningClose();
                    }}
                  >
                    Evet
                  </Button>
                  <Button onClick={onExpertiseTramerWarningClose}>Hayır</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}

          <Modal isOpen={isExpertiseTramer360ModalOpen} onClose={onExpertiseTramer360ModalClose} isCentered>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Trans>Detaylı Araç Sorgulama</Trans>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {!queryTramer360Before?.url && (
                  <Text>
                    <Trans>Detaylı Araç Sorgulama işlemi arka planda devam etmektedir.</Trans>
                  </Text>
                )}
                {queryTramer360Before?.url && (
                  <>
                    <Text mb={4}>
                      <Trans>Detaylı Araç Sorgulama işlemi tamamlandı.</Trans>
                    </Text>
                    <Button
                      as="a"
                      href={queryTramer360Before.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      colorScheme="blue"
                    >
                      <Trans>PDF&apos;i Görüntüle</Trans>
                    </Button>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button onClick={onExpertiseTramer360ModalClose}>Kapat</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}

      <Flex align="stretch" direction="column" basis="100%" gap="2">
        <Heading as="h4" size="md">
          <Trans>Kaporta Bilgileri</Trans>
        </Heading>
        <Spacer />
        <MasterNotesBanner
          brandId={customerVehicle?.brandId}
          modelId={customerVehicle?.modelId}
          modelYear={customerVehicle?.modelYear}
          noteType={ExpertiseDamageNoteType.Bodywork}
        />
        {!customerVehicle?.vehicleTypeId && (
          <Alert status="warning">
            <AlertIcon />
            <Trans>Araç Türü Kayıt Edilmeden Mikron Kayıt Alma ve Boya Değişen İşaretle işlemleri yapılamaz</Trans>
          </Alert>
        )}
        {!customerVehicle?.chassisNumber && (
          <Alert status="warning">
            <AlertIcon />
            <Trans>Şasi numarası girilmeden Hasar Kaydı Sorgulama yapılamaz</Trans>
          </Alert>
        )}
        <Stack direction={['column', 'column', null, 'row']} spacing="24px">
          <Button
            border={isExpertiseTramerResultCompleted ? '2px solid green' : 'none'}
            disabled={!customerVehicle?.chassisNumber || !isHasarKaydiEnabled}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.HasarKaydiSorgulama]}
            onClick={onExpertiseTramerButtonClicked}
          >
            <Trans>Hasar Sorgu</Trans>
          </Button>
          <Button
            border={isExpertiseTramer360ResultCompleted ? '2px solid green' : 'none'}
            disabled={!customerVehicle?.chassisNumber || !isHasarKaydiEnabled}
            hidden={
              !customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.DetayliAracSorgulamaTramer360]
            }
            onClick={() => {
              onExpertiseTramer360ModalOpen();
            }}
          >
            <Trans>Detaylı Araç Sorgu</Trans>
          </Button>
          <Button
            border={isMicronRecorderResultsCompleted ? '2px solid green' : 'none'}
            onClick={onMicronRecorderOpen}
            disabled={!customerVehicle?.vehicleTypeId}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.MikronKaydiFormu]}
          >
            <Trans>Mikron Kaydı</Trans>
          </Button>

          <Button
            border={isMicronRecorderResultsCompleted ? '2px solid green' : 'none'}
            disabled={!customerVehicle?.vehicleTypeId}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.MikronKaydiFormu]}
          >
            <Link href={micronAppUrl}>
              {' '}
              <Trans>Cihazla Mikron Kaydı</Trans>
            </Link>
          </Button>

          <Button
            border={isExpertiseResultsCompleted ? '2px solid green' : 'none'}
            onClick={onExpertiseOpen}
            disabled={!customerVehicle?.vehicleTypeId}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.BoyaDegisenFormu]}
          >
            <Trans>Boya Değişen İşaretle</Trans>
          </Button>

          <Button
            border={isChassisControlResultsCompleted ? '2px solid green' : 'none'}
            onClick={onChassisControlOpen}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.SasiKontrol]}
            disabled={!customerVehicle?.vehicleTypeId}
          >
            <Trans>Şasi Direk Kontrolleri</Trans>
          </Button>
          <Button
            border={isIcKontrolFormuCompleted ? '2px solid green' : 'none'}
            onClick={onIcKontrolOpen}
            disabled={!customerVehicle?.vehicleTypeId}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.IcKontrolFormu]}
          >
            <Trans>İç Kontroller</Trans>
          </Button>
          <Button
            border={isDisKontrolFormuCompleted ? '2px solid green' : 'none'}
            onClick={onDisKontrolOpen}
            disabled={!customerVehicle?.vehicleTypeId}
            hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.DisKontrolFormu]}
          >
            <Trans>Dış Kontroller</Trans>
          </Button>
        </Stack>
        <Spacer />

        <NoteAutoCompleteSelect
          customerId={customer.id}
          noteType={ExpertiseDamageNoteType.Bodywork}
          isDisabled={!canCustomerContinue}
          hidden={!customerAbilityDefinitionTable[ExpertisePackageAbilityDefinitionType.KaportaKontrolNotlari]}
        />
      </Flex>
    </>
  );
};
